import * as React from 'react';

// Reading preferences from the Accessibility options panel. They apply as classes on the root
// element (styles in font.css), so every page and every copy of the panel shares them.
// Nothing is sent anywhere: they live in memory for the visit, or in this browser's local
// storage when the visitor turns on "Remember on this device".

export const textSizes = ['default', 'large', 'larger', 'largest'] as const;
export type TextSize = (typeof textSizes)[number];
// "system" follows the device's light or dark setting. Saved choices from before it existed used
// "default" for light; that value is no longer accepted, so those visitors now follow the device.
export const contrasts = ['system', 'light', 'high', 'dark'] as const;
export type Contrast = (typeof contrasts)[number];
// The colours actually on screen once "system" is resolved.
export type ActiveContrast = Exclude<Contrast, 'system'>;

export type Preferences = {
  textSize: TextSize;
  textSpacing: boolean;
  readableFont: boolean;
  contrast: Contrast;
  underlineLinks: boolean;
  strongFocus: boolean;
  readingGuide: boolean;
  reduceMotion: boolean;
  remember: boolean;
};

export const defaultPreferences: Preferences = {
  textSize: 'default',
  textSpacing: false,
  readableFont: false,
  contrast: 'system',
  underlineLinks: false,
  strongFocus: false,
  readingGuide: false,
  reduceMotion: false,
  remember: false,
};

export const storageKey = 'scaledtech-accessibility';

const switchClasses = {
  textSpacing: 'site-text-spacing',
  readableFont: 'site-readable-font',
  underlineLinks: 'site-underline-links',
  strongFocus: 'site-strong-focus',
  readingGuide: 'site-reading-guide',
  reduceMotion: 'site-reduce-motion',
} as const;

export const resolveContrast = (contrast: Contrast, deviceDark: boolean): ActiveContrast =>
  contrast === 'system' ? (deviceDark ? 'dark' : 'light') : contrast;

export const rootClasses = (preferences: Preferences, deviceDark = false) => {
  const names: string[] = [];
  const contrast = resolveContrast(preferences.contrast, deviceDark);
  if (preferences.textSize !== 'default') names.push(`site-text-${preferences.textSize}`);
  if (contrast === 'high') names.push('site-high-contrast');
  if (contrast === 'dark') names.push('site-dark');
  for (const [key, name] of Object.entries(switchClasses))
    if (preferences[key as keyof typeof switchClasses]) names.push(name);
  return names;
};

const allRootClasses = [
  ...textSizes.filter((size) => size !== 'default').map((size) => `site-text-${size}`),
  'site-high-contrast',
  'site-dark',
  ...Object.values(switchClasses),
];

// Only known values are accepted, so a hand-edited or stale entry cannot inject anything.
export function parsePreferences(raw: string | null): Preferences | undefined {
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (!value || typeof value !== 'object') return undefined;
    const parsed: Preferences = { ...defaultPreferences, remember: true };
    if (textSizes.includes(value.textSize as TextSize))
      parsed.textSize = value.textSize as TextSize;
    if (contrasts.includes(value.contrast as Contrast))
      parsed.contrast = value.contrast as Contrast;
    for (const key of Object.keys(switchClasses) as (keyof typeof switchClasses)[])
      parsed[key] = value[key] === true;
    return parsed;
  } catch {
    return undefined;
  }
}

function readStorage(): Preferences | undefined {
  try {
    return parsePreferences(window.localStorage.getItem(storageKey));
  } catch {
    return undefined;
  }
}

function writeStorage(preferences: Preferences) {
  try {
    if (preferences.remember) {
      const { remember, ...saved } = preferences;
      void remember;
      window.localStorage.setItem(storageKey, JSON.stringify(saved));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch {
    // Storage can be blocked (private windows, site-data settings); the visit still works.
  }
}

let current: Preferences = defaultPreferences;
let loaded = false;
let deviceDark = false;
let stopWatchingDevice: (() => void) | undefined;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

function apply(preferences: Preferences) {
  const root = document.documentElement;
  const wanted = new Set(rootClasses(preferences, deviceDark));
  for (const name of allRootClasses) root.classList.toggle(name, wanted.has(name));
}

// Tracks the device's light or dark setting, including changes while the page is open (for
// example a phone switching to dark at sunset).
function watchDevice() {
  if (typeof window.matchMedia !== 'function') return;
  try {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    deviceDark = query.matches;
    const onChange = (event: MediaQueryListEvent) => {
      deviceDark = event.matches;
      apply(current);
      notify();
    };
    query.addEventListener('change', onChange);
    stopWatchingDevice = () => query.removeEventListener('change', onChange);
  } catch {
    deviceDark = false;
  }
}

function ensureLoaded() {
  if (loaded || typeof document === 'undefined') return;
  loaded = true;
  watchDevice();
  current = readStorage() ?? current;
  apply(current);
}

// Called before the app renders, so saved choices apply as early as the page script runs.
export function loadSavedPreferences() {
  ensureLoaded();
}

export function getPreferences() {
  ensureLoaded();
  return current;
}

export function setPreferences(update: Partial<Preferences>) {
  ensureLoaded();
  current = { ...current, ...update };
  apply(current);
  writeStorage(current);
  notify();
}

export function resetPreferences() {
  setPreferences(defaultPreferences);
}

// For tests: forget everything held in memory.
export function resetPreferencesForTests() {
  current = defaultPreferences;
  loaded = false;
  deviceDark = false;
  stopWatchingDevice?.();
  stopWatchingDevice = undefined;
  if (typeof document !== 'undefined')
    for (const name of allRootClasses) document.documentElement.classList.remove(name);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// The server and the first browser render both see the defaults, so hydration matches;
// React then re-renders with the visitor's choices.
export function usePreferences() {
  return React.useSyncExternalStore(subscribe, getPreferences, () => defaultPreferences);
}

const getActiveContrast = () => resolveContrast(getPreferences().contrast, deviceDark);

// The colours on screen. The server render cannot know the device setting, so it (and the first
// browser render) uses light; React then switches to the visitor's colours.
export function useActiveContrast(): ActiveContrast {
  return React.useSyncExternalStore(subscribe, getActiveContrast, () => 'light');
}
